import { useState } from 'react';

import TextOverflowTooltip from '@atom/components/common/tooltip/TextOverflowTooltip';
import { DatePicker, DateTimePicker } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { DataType } from '@atom/types/dataType';
import { WorkOrderField } from '@atom/types/work';
import { PREVIEW_FIELD_WIDTH } from '@atom/utilities/taskUtilities';

interface Props {
  field: WorkOrderField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  width?: string;
  requiredFieldIds?: Set<string>;
  requiredFieldName?: keyof WorkOrderField;
  isTemplate?: boolean;
  onClick?: () => void;
  error?: boolean;
  helperText?: string;
}

const WorkOrderDatePicker = ({
  field,
  onChange,
  isDisabled,
  width = PREVIEW_FIELD_WIDTH,
  requiredFieldIds = new Set([]),
  requiredFieldName,
  isTemplate,
  onClick,
  error,
  helperText,
}: Props) => {
  const [value, setValue] = useState<Date>(
    field.value ? new Date(field.value) : null,
  );

  const handleChange = (date?: Date) => {
    setValue(date);
    // date was removed entirely
    if (!date) {
      onChange(field.id, '');
    } else if (!Number.isNaN(date.valueOf())) {
      onChange(field.id, date.valueOf());
    }
  };

  const props = {
    value,
    onChange: handleChange,
    disabled: isDisabled,
    onClick,
    style: { width },
    error,
    helperText,
    'data-cy': `workInfoField_${field.title}_DateInput`,
  };

  const content =
    field.dataType === DataType.DATE ? (
      <DatePicker {...props} />
    ) : (
      <DateTimePicker {...props} />
    );

  const labelColor = requiredFieldIds.has(field.id)
    ? colors.brand.red
    : colors.neutral.dim;

  // template displays asterisk on required or requiredOnCreate
  const text =
    (isTemplate && (field.required || field.requiredOnCreate)) ||
    !!field[requiredFieldName]
      ? `* ${field?.title}`
      : field?.title;

  return (
    <div onClick={onClick}>
      <TextOverflowTooltip
        width={width}
        text={text}
        color={labelColor}
        fontSize={fonts.sm}
      />
      {content}
    </div>
  );
};

export default WorkOrderDatePicker;
